// Playground tool registry. Deliberately minimal — every tool that earns
// its place here makes team chat materially better. Three general-purpose
// tools, plus an Accelerando ERP block that only appears in the system
// prompt when the user has configured a base URL.
//
//   fetch_url(url)              — read a web page's text content
//   fetch_github_file(o,r,p)    — read a file from a GitHub repo
//   current_time(tz?)           — wall-clock time, optionally TZ'd
//
//   list_invoices(status?)      — query the configured tenant's invoices
//   read_invoice(id)            — pull a single .agi record
//   create_invoice(...)         — create one as draft (unless overridden)
//   update_invoice_status(...)  — transition draft → sent → paid
//
// All tools are async. Output is capped so a tool can't blow out the
// model's context window — the loop is supposed to converge, not
// drown the model in attached documents.

import { useGitHubStore } from "../store/githubStore";
import { useAccelerandoStore } from "../store/accelerandoStore";

const MAX_TOOL_OUTPUT = 10_000;

export interface ToolSpec {
  name: string;
  description: string;
  /** Free-text argument doc — embedded in the system prompt. */
  argsDoc: string;
}

export interface ToolResult {
  ok: boolean;
  /** Human-readable result that flows back to the model. Capped. */
  detail: string;
}

const BASE_TOOLS: ToolSpec[] = [
  {
    name: "fetch_url",
    description: "Fetch a public web page and return its visible text (HTML stripped). Use for summarizing articles, PRs, docs, or anything reachable by URL.",
    argsDoc: '{"url": "https://…"}',
  },
  {
    name: "fetch_github_file",
    description: "Fetch the raw contents of a single file from any GitHub repo your PAT can see. Use for reading source files, READMEs, configs that channel members reference.",
    argsDoc: '{"owner": "octocat", "repo": "hello-world", "path": "README.md"}',
  },
  {
    name: "current_time",
    description: "Return the current wall-clock time, optionally in a specific IANA timezone. Use for scheduling, recency questions, distributed-team coordination.",
    argsDoc: '{"tz": "America/Los_Angeles"}  // tz is optional',
  },
];

const ACCELERANDO_TOOLS: ToolSpec[] = [
  {
    name: "list_invoices",
    description: "List invoices for the configured Accelerando tenant. Optional status filter (draft, sent, paid, void) and/or customer_id filter. Use when the user asks about AR, outstanding bills, or one customer's billing.",
    argsDoc: '{"status": "sent", "customer_id": "cus_001"}  // both filters optional, compose with AND',
  },
  {
    name: "read_invoice",
    description: "Read one Accelerando invoice by id, returning the full .agi record including customer, totals, dates, and notes. Line items are separate — use list_line_items.",
    argsDoc: '{"id": "8f3b2e1a-..."}',
  },
  {
    name: "create_invoice",
    description: "Create a new draft invoice in Accelerando for a customer. status defaults to 'draft'. Confirm the total with the user before calling.",
    argsDoc: '{"customer_id": "cus_001", "total": 1500, "notes": "Q3 services", "due_date": "2026-07-25"}  // notes/due_date optional, status defaults to draft',
  },
  {
    name: "update_invoice_status",
    description: "Transition an Accelerando invoice between statuses (draft → sent → paid, or → void). Use this rather than a generic edit when changing the status.",
    argsDoc: '{"id": "8f3b2e1a-...", "status": "paid"}',
  },
  {
    name: "edit_invoice",
    description: "Partial edit of an invoice's HEADER fields (notes / due_date / customer_id / employee_id). NOT for status (use update_invoice_status — auto-stamps paid_date) and NOT for total (computed from lines via edit_invoice_lines). Omit a field to leave it. Empty-string due_date clears the date.",
    argsDoc: '{"id": "8f3b2e1a-...", "notes": "Quarter-end push terms 2/10 net 30", "due_date": "2026-08-15"}',
  },
  {
    name: "delete_invoice",
    description: "Permanently remove an invoice AND all of its line items in one atomic git commit. Audit log shows 'delete Invoice X (+N lines)'. Prefer status='void' for issued invoices — delete is for drafts created in error or stale test data.",
    argsDoc: '{"id": "8f3b2e1a-..."}',
  },
  {
    name: "edit_customer",
    description: "Partial edit of a customer's mutable fields (name, email, phone, company, tier, active). Empty-string clears. To retire a relationship without orphaning history, set active=false instead of deleting.",
    argsDoc: '{"id": "cus_001", "name": "Globex Corp.", "tier": "premium", "active": true}',
  },
  {
    name: "import_customers",
    description: "Atomically create up to 100 customer rows in one git commit. Each row needs at least name. Use when onboarding from a CSV / spreadsheet — keeps the audit log clean as one row per import instead of N.",
    argsDoc: '{"rows": [{"name": "Globex", "tier": "premium"}, {"name": "Initech"}]}',
  },
  {
    name: "import_invoices",
    description: "Atomically create up to 100 invoice header rows in one commit. Each row needs total plus EITHER customer_id OR customer_name (ambiguous name = abort). Optional status / notes / due_date / paid_date. NOT for line items — use create_invoice_with_lines.",
    argsDoc: '{"rows": [{"customer_name": "Globex", "total": 1500, "status": "paid"}]}',
  },
  {
    name: "import_tickets",
    description: "Atomically create up to 100 service tickets in one commit. Each row needs subject plus EITHER customer_id OR customer_name (ambiguous = abort). Optional description / priority / status / assignee_id.",
    argsDoc: '{"rows": [{"customer_name": "Initech", "subject": "Printer down", "priority": "high"}]}',
  },
  {
    name: "import_journal_entries",
    description: "Atomically create up to 100 single-debit / single-credit journal entries in one commit — common case for migrating books. Each row produces one entry + two lines of equal amount. Required: description, debit_account, credit_account, amount. For multi-line entries use create_journal_entry.",
    argsDoc: '{"rows": [{"description": "Cash receipt Globex", "debit_account": "1000-Cash", "credit_account": "1200-AR", "amount": 1500}]}',
  },
  {
    name: "list_accounts",
    description: "List the tenant's chart of accounts, ordered by code. Returns {code, name, type, active}. Advisory only — journal entries don't enforce membership in this chart yet.",
    argsDoc: '{"active_only": true}',
  },
  {
    name: "create_account",
    description: "Add an account to the chart. code must be unique per tenant. type is asset / liability / equity / revenue / expense — drives where the account shows on the eventual trial-balance and P&L tools.",
    argsDoc: '{"code": "5000-COGS", "name": "Cost of Goods Sold", "type": "expense"}',
  },
  {
    name: "edit_account",
    description: "Partial edit of a chart account. Rename code / name, reclassify type, or set active=false to retire. Code changes do NOT retroactively update existing journal lines.",
    argsDoc: '{"id": "acc_001", "name": "Operating Cash (renamed)", "active": false}',
  },
  {
    name: "delete_account",
    description: "Permanently remove a chart account. Setting active=false via edit_account is usually preferred — existing journal lines referencing the code keep bare-string history after delete.",
    argsDoc: '{"id": "acc_001"}',
  },
  {
    name: "list_bank_transactions",
    description: "List bank statement transactions, newest first. Filter by account, date window, and/or reconciled status ('yes'/'no'). Amount is signed: + deposit, - withdrawal.",
    argsDoc: '{"account": "1000-Cash", "reconciled": "no"}',
  },
  {
    name: "import_bank_transactions",
    description: "Atomically create up to 100 bank transactions in one commit. Each row needs account + date + description + signed amount. Optional reference.",
    argsDoc: '{"rows": [{"account": "1000-Cash", "date": "2026-04-05", "description": "DEPOSIT GLOBEX", "amount": 1500}]}',
  },
  {
    name: "reconcile_bank_transaction",
    description: "Pair a bank transaction with a JournalLine on the same account. Validates account match. The journal line is not modified — only the bank tx carries the reconciled_with pointer.",
    argsDoc: '{"id": "bt_001", "journal_line_id": "jl_001"}',
  },
  {
    name: "unreconcile_bank_transaction",
    description: "Clear the reconciled_with pointer on a bank transaction.",
    argsDoc: '{"id": "bt_001"}',
  },
  {
    name: "bank_reconciliation",
    description: "Reconciliation view for one account over a window. Returns {matched, unmatched_bank, unmatched_lines, summary: {bank_total, ledger_total, diff}}. The diff is the headline number to drive to zero.",
    argsDoc: '{"account": "1000-Cash", "from": "2026-04-01T00:00:00Z", "to": "2026-04-30T23:59:59Z"}',
  },
  {
    name: "close_period",
    description: "Lock every journal-write path against entries dated on/before through_date. Subsequent create/import/edit/reverse and auto-post reject when their entry date falls in a closed period. Use at month/quarter end after reports are finalized.",
    argsDoc: '{"through_date": "2026-03-31T23:59:59Z", "notes": "Q1 2026 close — reports signed off"}',
  },
  {
    name: "list_period_closes",
    description: "List PeriodClose records, most recent through_date first. The latest one governs.",
    argsDoc: '{}',
  },
  {
    name: "reopen_period",
    description: "Delete a PeriodClose record, removing its lock. The deletion enters the audit log. If multiple closes exist, the next-latest becomes the governing close.",
    argsDoc: '{"id": "pc_001"}',
  },
  {
    name: "ar_aging",
    description: "Accounts receivable aging. Buckets every unpaid invoice into 0-30 / 31-60 / 61-90 / 90+ days based on (as_of - due_date), falling back to created_at when due_date missing. Returns rows per customer + grand totals. Sorted by total desc.",
    argsDoc: '{"as_of": "2026-04-30T23:59:59Z"}',
  },
  {
    name: "list_vendors",
    description: "List vendors for a tenant. Up to 100.",
    argsDoc: '{}',
  },
  {
    name: "create_vendor",
    description: "Create a new vendor record (AP-side mirror of Customer). Active defaults true.",
    argsDoc: '{"name": "Pacific Gas", "email": "ar@pge.com"}',
  },
  {
    name: "edit_vendor",
    description: "Partial edit of vendor mutable fields. active=false to retire without deleting.",
    argsDoc: '{"id": "v_001", "phone": "555-1212"}',
  },
  {
    name: "read_vendor",
    description: "Read a single vendor by id.",
    argsDoc: '{"id": "v_001"}',
  },
  {
    name: "delete_vendor",
    description: "Permanently remove a vendor. REFUSES if any bills reference it — use edit_vendor active=false to retire instead.",
    argsDoc: '{"id": "v_001"}',
  },
  {
    name: "list_bills",
    description: "List bills (vendor payables), optional status / vendor_id filters. Each row carries vendor_name.",
    argsDoc: '{"status": "open"}',
  },
  {
    name: "create_bill",
    description: "Record a vendor bill (payable). Status defaults 'open'. Bills are vendor-side invoices — what you owe.",
    argsDoc: '{"vendor_id": "v_001", "total": 250, "due_date": "2026-05-01"}',
  },
  {
    name: "update_bill_status",
    description: "Transition a bill between open / paid / void. Auto-stamps paid_date on paid.",
    argsDoc: '{"id": "b_001", "status": "paid"}',
  },
  {
    name: "edit_bill",
    description: "Partial edit of a bill's header (notes, due_date, vendor_id, reference). NOT status — use update_bill_status.",
    argsDoc: '{"id": "b_001", "notes": "PO #4421"}',
  },
  {
    name: "read_bill",
    description: "Read a single bill record by id.",
    argsDoc: '{"id": "b_001"}',
  },
  {
    name: "delete_bill",
    description: "Permanently remove a bill. Prefer status='void' for bills received in error — delete is for duplicates or test data.",
    argsDoc: '{"id": "b_001"}',
  },
  {
    name: "import_bills",
    description: "Atomically create up to 100 bills in one commit. Each row needs total plus EITHER vendor_id OR vendor_name. Optional status / notes / due_date / reference.",
    argsDoc: '{"rows": [{"vendor_name": "Pacific Gas", "total": 250, "due_date": "2026-05-01"}]}',
  },
  {
    name: "ap_aging",
    description: "Accounts payable aging — mirror of ar_aging on the payables side. Buckets open bills into 0-30 / 31-60 / 61-90 / 90+ days, groups by vendor.",
    argsDoc: '{"as_of": "2026-04-30T23:59:59Z"}',
  },
  {
    name: "list_recurring_invoices",
    description: "List recurring-invoice templates, ordered by next_run ascending.",
    argsDoc: '{}',
  },
  {
    name: "create_recurring_invoice",
    description: "Create a monthly recurring invoice template. The nightly cron emits an Invoice on day_of_month and advances next_run. status_on_create defaults to sent; due_days_after defaults to 30.",
    argsDoc: '{"customer_id": "cus_001", "total": 500, "day_of_month": 1, "next_run": "2026-05-01T06:00:00Z"}',
  },
  {
    name: "edit_recurring_invoice",
    description: "Partial edit of a recurring template. active=false pauses without deleting.",
    argsDoc: '{"id": "ri_001", "active": false}',
  },
  {
    name: "delete_recurring_invoice",
    description: "Permanently remove a recurring template. Past invoices stay.",
    argsDoc: '{"id": "ri_001"}',
  },
  {
    name: "run_recurring_invoices_now",
    description: "Manually fire the recurring-invoice cron for this tenant — useful for verifying templates without waiting until 06:30 UTC.",
    argsDoc: '{}',
  },
  {
    name: "list_recurring_bills",
    description: "List recurring-bill templates, ordered by next_run ascending.",
    argsDoc: '{}',
  },
  {
    name: "create_recurring_bill",
    description: "Create a monthly recurring bill template (utilities, rent, subscriptions). The nightly cron emits a Bill (status=open) on day_of_month and advances next_run. due_days_after defaults to 30.",
    argsDoc: '{"vendor_id": "ven_001", "total": 220, "day_of_month": 10, "next_run": "2026-05-10T06:00:00Z", "expense_account": "5100-Utilities"}',
  },
  {
    name: "edit_recurring_bill",
    description: "Partial edit of a recurring-bill template. active=false pauses without deleting.",
    argsDoc: '{"id": "rb_001", "active": false}',
  },
  {
    name: "delete_recurring_bill",
    description: "Permanently remove a recurring-bill template. Past bills stay.",
    argsDoc: '{"id": "rb_001"}',
  },
  {
    name: "run_recurring_bills_now",
    description: "Manually fire the recurring-bill cron for this tenant.",
    argsDoc: '{}',
  },
  {
    name: "list_attachments",
    description: "List attachments for one parent record. Returns metadata {filename, content_type, size_bytes, r2_key, notes}. Actual blob lives in R2 — fetch via GET /attachments/:id/download. Parent_entity ∈ Invoice / Bill / JournalEntry / ServiceTicket / Customer / Vendor.",
    argsDoc: '{"parent_entity": "Invoice", "parent_id": "inv_001"}',
  },
  {
    name: "delete_attachment",
    description: "Permanently remove an attachment metadata record. The R2 blob itself is removed by the worker's DELETE /attachments/:id endpoint — this tool just drops the .agi record (audit-logged).",
    argsDoc: '{"id": "att_001"}',
  },
  {
    name: "trial_balance",
    description: "Per-account sum of debits and credits across all journal lines, joined to ChartOfAccount for type+name. Returns rows + totals with a balanced flag. Optional as_of caps the window. Accounts not in chart show as type='unclassified'.",
    argsDoc: '{"as_of": "2026-04-30T23:59:59Z"}',
  },
  {
    name: "list_journal_lines",
    description: "List debit/credit lines. Pass entry_id to scope to one entry's lines, OR pass account to drill into an account ledger across all entries (joined with date/description/reference; newest first). Optional from/to window the account-scoped ledger.",
    argsDoc: '{"account": "1200-AR", "from": "2026-04-01T00:00:00Z", "to": "2026-04-30T23:59:59Z"}',
  },
  {
    name: "profit_and_loss",
    description: "Revenue + expense totals over a window. Returns {revenue, expenses, revenue_total, expense_total, net_income}. Requires from + to ISO dates. Excludes unclassified accounts.",
    argsDoc: '{"from": "2026-04-01T00:00:00Z", "to": "2026-04-30T23:59:59Z"}',
  },
  {
    name: "delete_customer",
    description: "Permanently remove a customer record. REFUSES if any invoice or ticket references the customer. Use edit_customer active=false to retire without deletion.",
    argsDoc: '{"id": "cus_001"}',
  },
  {
    name: "edit_journal_entry",
    description: "Partial edit of a journal entry's HEADER (description, reference, date). NOT for debit/credit amounts — those stay sum-to-zero; post a reversing entry instead.",
    argsDoc: '{"id": "je_001", "description": "Cash receipt from Globex (corrected)"}',
  },
  {
    name: "reverse_journal_entry",
    description: "Post a reversing journal entry: new entry with debit/credit swapped at same amounts, description='Reverses: <original>', reference='REV-<id>'. Original stays intact. The standard accounting move when books are wrong.",
    argsDoc: '{"id": "je_001", "memo": "double-posted"}',
  },
  {
    name: "delete_finding",
    description: "Permanently remove one OIE finding. Use when an analyst paragraph is stale, wrong, or redundant. The delete event itself enters the audit log.",
    argsDoc: '{"id": "f_001"}',
  },
  {
    name: "delete_findings_older_than",
    description: "Bulk-prune findings older than a cutoff. Pass either `days` (relative, e.g. 30) or `before` (ISO date). One atomic git commit. Optional baby filter.",
    argsDoc: '{"days": 30, "baby": "ar"}',
  },
  {
    name: "create_invoice_with_lines",
    description: "ATOMIC: create an invoice and its line items in one git commit. Total is COMPUTED server-side — do NOT pass total. Prefer this over create_invoice + N add_line_item calls when you know the lines upfront.",
    argsDoc: '{"customer_id": "cus_001", "lines": [{"description": "Consulting hours", "qty": 10, "unit_price": 150}, {"description": "Travel", "unit_price": 350}], "notes": "Q3 services"}  // qty defaults to 1; line_total computed from qty*unit_price',
  },
  {
    name: "edit_invoice_lines",
    description: "ATOMIC: edit and/or append line items on an existing invoice in one git commit. Each line is an object with optional `id` (present → edit, absent → create). Lines NOT in the array stay untouched (this tool doesn\\'t delete). Server recomputes line_total per row and invoice.total across all rows.",
    argsDoc: '{"invoice_id": "8f3b2e1a-...", "lines": [{"id": "c91d7e5f-...", "qty": 12}, {"description": "Discount", "unit_price": -100}]}  // edits the first, appends the second',
  },
  {
    name: "read_line_item",
    description: "Read one line item by id. The returned record carries invoice_id, so this is the way to recover the parent invoice when you only have a line-item identifier (e.g. from an activity-feed path).",
    argsDoc: '{"id": "c91d7e5f-..."}',
  },
  {
    name: "list_line_items",
    description: "List the line items on a specific invoice. line_total per row is precomputed.",
    argsDoc: '{"invoice_id": "8f3b2e1a-..."}',
  },
  {
    name: "add_line_item",
    description: "Append a line to an invoice. The server computes line_total = qty × unit_price — do NOT pass line_total yourself. qty defaults to 1.",
    argsDoc: '{"invoice_id": "8f3b2e1a-...", "description": "Consulting hours", "qty": 10, "unit_price": 150}',
  },
  {
    name: "list_customers",
    description: "List customers for the configured tenant (capped at 100). When the user names a company, list and search the result rather than guessing an id.",
    argsDoc: '{}  // no args required beyond tenant',
  },
  {
    name: "read_customer",
    description: "Read one customer by id, returning the full .agi record. Pair with list_invoices(customer_id) / list_tickets(customer_id) for a customer-360 picture.",
    argsDoc: '{"id": "30cf071b-..."}',
  },
  {
    name: "create_customer",
    description: "Create a new customer. name is required; everything else optional. tier defaults to 'standard'.",
    argsDoc: '{"name": "Globex Corporation", "email": "ap@globex.test", "company": "Globex Corporation", "tier": "premium"}',
  },
  {
    name: "list_tickets",
    description: "List service tickets, optionally filtered by status (new, open, pending, resolved, closed) and/or customer_id.",
    argsDoc: '{"status": "new", "customer_id": "cus_001"}  // both filters optional',
  },
  {
    name: "create_ticket",
    description: "Open a service ticket for a customer. priority defaults to 'normal'; status defaults to 'new'.",
    argsDoc: '{"customer_id": "cus_001", "subject": "Server crash Friday", "description": "Logs attached", "priority": "high"}',
  },
  {
    name: "update_ticket_status",
    description: "Transition a ticket's status. Setting to 'resolved' stamps resolved_at automatically.",
    argsDoc: '{"id": "tkt_...", "status": "resolved"}',
  },
  {
    name: "read_ticket",
    description: "Read one ticket by id, returning the full .agi record (subject, description, priority, assignee, status, dates).",
    argsDoc: '{"id": "tkt_..."}',
  },
  {
    name: "edit_ticket",
    description: "Partial edit of an existing ticket's mutable fields (subject / description / priority / assignee_id). For status, use update_ticket_status — it auto-stamps resolved_at. Omit a field to leave it. Empty-string assignee_id unassigns.",
    argsDoc: '{"id": "tkt_...", "subject": "Server crash — root cause confirmed", "priority": "high", "assignee_id": "emp_42"}',
  },
  {
    name: "delete_ticket",
    description: "Permanently delete a ticket. Records a deletion entry in the audit log (git log shows `delete ServiceTicket X`). Use sparingly — a status transition to closed is usually the right move. CONFIRM with the user before calling.",
    argsDoc: '{"id": "tkt_..."}',
  },
  {
    name: "total_outstanding",
    description: "Accounts receivable: sum + count of sent (not yet paid) invoices. Use when the user asks 'how much is owed' or 'what's our AR'.",
    argsDoc: '{}  // no args beyond tenant',
  },
  {
    name: "revenue_by_month",
    description: "Paid revenue grouped by month (YYYY-MM), newest first. Defaults to 12 months. Use for 'how was last quarter', 'show me revenue history', or similar.",
    argsDoc: '{"months": 6}  // months is optional, defaults to 12, max 60',
  },
  {
    name: "tickets_by_priority",
    description: "Counts of OPEN service tickets grouped by priority (urgent first). Use for triage, standup updates, 'what should I work on'.",
    argsDoc: '{}  // no args beyond tenant',
  },
  {
    name: "list_findings",
    description: "List recent OIE findings — short analyst paragraphs written by specialist BabyAI babies that read the audit log. Use when the user asks 'what is the OIE saying', 'any insights', or 'what changed lately'.",
    argsDoc: '{"baby": "ar", "limit": 5}  // both optional; baby filters by analyst name',
  },
  {
    name: "create_journal_entry",
    description: "ATOMIC: create a journal entry and its debit/credit lines in one git commit. Server REJECTS the call before any write if sum(debits) ≠ sum(credits). Use for any transaction that needs both sides recorded at once.",
    argsDoc: '{"description": "Cash receipt from Globex", "reference": "AR-2026-Q3-001", "lines": [{"account": "1000-Cash", "debit": 1500}, {"account": "1200-AR Globex", "credit": 1500}]}',
  },
  {
    name: "list_journal_entries",
    description: "List journal entries for the tenant, newest first. Optional reference filter.",
    argsDoc: '{"reference": "AR-2026-Q3-001"}  // optional',
  },
  {
    name: "read_journal_entry",
    description: "Read one journal entry header. Use list_journal_lines to get its debits and credits.",
    argsDoc: '{"id": "je_..."}',
  },
  {
    name: "who_changed",
    description: "Real GitHub commit history for one record. Returns commits that touched data/<tenant>/<Entity>/<id>.agi, newest first, with author/date/message/sha. Use for 'when did this status change', 'who edited this last', 'show me the audit history'. Note: only commits reconciled by the */15 syncer show up — very recent writes may be R2-only.",
    argsDoc: '{"entity": "Invoice", "id": "8f3b2e1a-...", "limit": 10}  // entity ∈ Customer / Invoice / InvoiceLineItem / ServiceTicket / JournalEntry / JournalLine / OieFinding',
  },
];

const ACCELERANDO_TOOL_NAMES = new Set(ACCELERANDO_TOOLS.map((t) => t.name));

/**
 * The tool catalog visible to the model. Accelerando tools appear only when
 * the user has configured a base URL — surfacing tools the model can't
 * actually call would invite hallucinated invoice numbers. Recomputed per
 * chat turn so toggling config in the Keys modal takes effect immediately.
 */
export function getTools(): ToolSpec[] {
  const { baseUrl } = useAccelerandoStore.getState();
  return baseUrl ? [...BASE_TOOLS, ...ACCELERANDO_TOOLS] : BASE_TOOLS;
}

export async function executeTool(name: string, args: unknown): Promise<ToolResult> {
  try {
    switch (name) {
      case "fetch_url":         return await runFetchUrl(args as { url?: unknown });
      case "fetch_github_file": return await runFetchGitHubFile(args as { owner?: unknown; repo?: unknown; path?: unknown });
      case "current_time":      return runCurrentTime(args as { tz?: unknown });
      default:
        if (ACCELERANDO_TOOL_NAMES.has(name)) {
          return await runAccelerandoTool(name, args as Record<string, unknown>);
        }
        return { ok: false, detail: `unknown tool: ${name}` };
    }
  } catch (e) {
    return { ok: false, detail: `tool execution threw: ${e instanceof Error ? e.message : String(e)}` };
  }
}

async function runAccelerandoTool(name: string, args: Record<string, unknown>): Promise<ToolResult> {
  const { baseUrl, tenantId, apiKey } = useAccelerandoStore.getState();
  if (!baseUrl) {
    return { ok: false, detail: "Accelerando base URL not configured — open the Keys modal and paste the worker URL." };
  }
  if (!tenantId) {
    return { ok: false, detail: "Accelerando tenant_id not configured — open the Keys modal and set it." };
  }
  if (!apiKey) {
    return { ok: false, detail: "Accelerando API key not configured — open the Keys modal and paste the Bearer token." };
  }

  let resp: Response;
  try {
    resp = await fetch(`${baseUrl}/tools/${encodeURIComponent(name)}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ tenant_id: tenantId, ...args }),
    });
  } catch (e) {
    return { ok: false, detail: `accelerando unreachable: ${e instanceof Error ? e.message : String(e)}` };
  }
  if (resp.status === 401) {
    return { ok: false, detail: "Accelerando rejected the API key (401). Re-paste it in the Keys modal." };
  }

  interface ToolEnvelope {
    ok?: unknown;
    value?: unknown;
    error?: unknown;
  }
  let body: ToolEnvelope;
  try {
    body = (await resp.json()) as ToolEnvelope;
  } catch {
    return { ok: false, detail: `accelerando returned non-JSON (HTTP ${resp.status})` };
  }
  if (body.ok !== true) {
    const err = typeof body.error === "string" ? body.error : `HTTP ${resp.status}`;
    return { ok: false, detail: err };
  }
  const formatted = typeof body.value === "string"
    ? body.value
    : JSON.stringify(body.value, null, 2);
  return { ok: true, detail: cap(formatted) };
}

async function runFetchUrl(args: { url?: unknown }): Promise<ToolResult> {
  const url = typeof args.url === "string" ? args.url : null;
  if (!url) return { ok: false, detail: 'arg "url" (string) is required' };
  if (!/^https?:\/\//.test(url)) return { ok: false, detail: "url must start with http:// or https://" };

  let resp: Response;
  try {
    resp = await fetch(url);
  } catch (e) {
    return {
      ok: false,
      detail: `fetch failed (likely CORS — many sites don't allow browser cross-origin reads): ${e instanceof Error ? e.message : String(e)}`,
    };
  }
  if (!resp.ok) return { ok: false, detail: `HTTP ${resp.status} ${resp.statusText}` };

  const ct = resp.headers.get("content-type") ?? "";
  const text = await resp.text();
  const visible = ct.includes("html") ? stripHtml(text) : text;
  return { ok: true, detail: cap(visible) };
}

async function runFetchGitHubFile(args: { owner?: unknown; repo?: unknown; path?: unknown }): Promise<ToolResult> {
  const owner = typeof args.owner === "string" ? args.owner : null;
  const repo = typeof args.repo === "string" ? args.repo : null;
  const path = typeof args.path === "string" ? args.path : null;
  if (!owner || !repo || !path) {
    return { ok: false, detail: 'args "owner", "repo", and "path" (all strings) are required' };
  }
  const token = useGitHubStore.getState().token;
  if (!token) return { ok: false, detail: "no GitHub PAT — configure one via the Sync modal first" };

  const url = `https://api.github.com/repos/${owner}/${repo}/contents/${encodeURIComponent(path).replace(/%2F/g, "/")}`;
  const resp = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
    },
  });
  if (!resp.ok) {
    let body = "";
    try { body = (await resp.text()).slice(0, 200); } catch { /* ignore */ }
    return { ok: false, detail: `GitHub ${resp.status} ${resp.statusText}${body ? ` — ${body}` : ""}` };
  }
  type ContentsResponse = { content: string; encoding: string };
  const json = (await resp.json()) as ContentsResponse;
  const cleaned = json.content.replace(/\s+/g, "");
  let decoded: string;
  try {
    const bin = atob(cleaned);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    decoded = new TextDecoder().decode(bytes);
  } catch (e) {
    return { ok: false, detail: `failed to decode file contents: ${e instanceof Error ? e.message : String(e)}` };
  }
  return { ok: true, detail: cap(decoded) };
}

function runCurrentTime(args: { tz?: unknown }): ToolResult {
  const tz = typeof args.tz === "string" ? args.tz : undefined;
  try {
    const opts: Intl.DateTimeFormatOptions = {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZoneName: "short",
    };
    if (tz) opts.timeZone = tz;
    const formatted = new Date().toLocaleString("en-US", opts);
    return { ok: true, detail: tz ? `${formatted}  (${tz})` : formatted };
  } catch (e) {
    return { ok: false, detail: `invalid timezone "${tz ?? ""}": ${e instanceof Error ? e.message : String(e)}` };
  }
}

function stripHtml(html: string): string {
  // DOMParser is the boring-correct way to get visible text from
  // arbitrary HTML in a browser context — handles entities, comments,
  // script/style elision automatically.
  if (typeof DOMParser === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  // Drop script/style nodes — their text content isn't visible.
  doc.querySelectorAll("script, style, noscript").forEach((n) => n.remove());
  const text = doc.body?.textContent ?? "";
  return text.replace(/\n\s*\n\s*\n+/g, "\n\n").trim();
}

function cap(s: string): string {
  if (s.length <= MAX_TOOL_OUTPUT) return s;
  return s.slice(0, MAX_TOOL_OUTPUT) + `\n\n[truncated — original was ${s.length} chars]`;
}
